import AdvanceTable from "components/common/advance-table/AdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";

const statusColors = {
  REQUESTED: { backgroundColor: "#FFF7E6", color: "#D48806", border: "#FFE7BA" },
  APPROVED: { backgroundColor: "#E6F4FF", color: "#1677FF", border: "#BAE0FF" },
  SCHEDULED: { backgroundColor: "#F6FFED", color: "#389E0D", border: "#D9F7BE" },
  FAILED: { backgroundColor: "#FFF1F0", color: "#CF1322", border: "#FFA39E" },
  DELETED: { backgroundColor: "#F9F0FF", color: "#722ED1", border: "#D3ADF7" },
};

const formatDate = (value) => {
  if (!value) return "None";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Invalid date";
  return date.toLocaleString();
};

export default function UserDeletionRequestsTable({
  requests,
  page,
  pageSize,
  setPage,
  setPageSize,
  totalPages,
  totalElements,
  onView,
  onApprove,
  onOpenSchedule,
  onOpenFail,
  onMarkDeleted,
  onDelete,
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionTargetId, setDeletionTargetId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDelete = (id) => {
    setDeletionTargetId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletionTargetId) return;
    setIsDeleting(true);
    try {
      await onDelete(deletionTargetId);
      setShowDeleteModal(false);
      setDeletionTargetId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatShortId = (value) => {
    if (!value) return "None";
    if (value.length <= 12) return value;
    return `${value.slice(0, 12)}...`;
  };

  const columns = [
    {
      accessorKey: "id",
      header: "Request ID",
      cell: ({ getValue }) => (
        <span
          className="text-500 d-inline-block"
          style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}
        >
          {formatShortId(getValue())}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "userId",
      header: "User ID",
      cell: ({ getValue }) => (
        <span
          className="text-500 d-inline-block"
          style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}
        >
          {formatShortId(getValue())}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue() || "UNKNOWN";
        const color = statusColors[value] || {
          backgroundColor: "#F5F5F5",
          color: "#595959",
          border: "#D9D9D9",
        };

        return (
          <span
            style={{
              backgroundColor: color.backgroundColor,
              color: color.color,
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "0.7rem",
              border: `1px solid ${color.border}`,
              fontWeight: 600,
            }}
          >
            {value}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "deletionReason",
      header: "Deletion Reason",
      cell: ({ getValue }) => (
        <span
          className="d-inline-block"
          style={{
            color: "#343a40",
            fontSize: "0.75rem",
            fontWeight: 500,
            lineHeight: 1.4,
            maxWidth: 260,
          }}
        >
          {getValue() || "None"}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "requestedAt",
      header: "Requested At",
      cell: ({ getValue }) => <span style={{ fontSize: "0.75rem" }}>{formatDate(getValue())}</span>,
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;

        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item onClick={() => onView(item.id)}>View</Dropdown.Item>
              <Dropdown.Item onClick={() => onApprove(item.id)}>
                Approve
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onOpenSchedule(item)}>
                Schedule Deletion
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onOpenFail(item)}>
                Mark Failed
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onMarkDeleted(item.id)}>
                Mark Deleted
              </Dropdown.Item>
              <Dropdown.Item
                className="text-danger"
                onClick={() => confirmDelete(item.id)}
              >
                Delete
              </Dropdown.Item>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: requests,
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          headerClassName="bg-300 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap fs-12"
          tableProps={{
            bordered: true,
            striped: true,
            className: "mb-0 overflow-hidden",
          }}
        />
        <div className="mt-3">
          <TablePaginationFooter
            page={page}
            totalPages={totalPages || 0}
            totalElements={totalElements || 0}
            onPrev={
              page > 0 && totalElements > 0
                ? () => setPage((prev) => Math.max(prev - 1, 0))
                : undefined
            }
            onNext={
              page < totalPages - 1 && totalElements > 0
                ? () => setPage((prev) => prev + 1)
                : undefined
            }
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            pageSizeOptions={[10, 20, 30, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>

      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDeleteConfirm}
        itemName="user deletion request"
        loading={isDeleting}
      />
    </>
  );
}
